import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Play, Lock, Loader2, AudioLines, Check } from "lucide-react";
import Seo from "@/components/Seo";
import KindText from "@/components/KindK";
import { ShareButton } from "@/components/ShareButton";
import { api } from "@/lib/api";
import { toast } from "sonner";

function VoicePicker() {
  const [info, setInfo] = useState(null);
  const [sel, setSel] = useState("shimmer");
  const [loading, setLoading] = useState("");
  const [busy, setBusy] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/film/voice").then(({ data }) => { setInfo(data); setSel(data.voice); }).catch(() => {});
  }, []);

  const preview = async (v) => {
    setSel(v); setLoading(v);
    try {
      const { data } = await api.post("/film/voice/preview", { voice: v });
      if (audioRef.current) { audioRef.current.src = data.audio; await audioRef.current.play(); }
    } catch { toast.error("Couldn't load the voice sample — try again."); }
    finally { setLoading(""); }
  };

  const poll = () => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/film/voice");
        if (!data.rendering) { clearInterval(t); setBusy(false); toast.success("Film re-rendered 🎬 — reload to watch"); }
      } catch { clearInterval(t); setBusy(false); }
    }, 6000);
  };

  const lock = async () => {
    setBusy(true);
    try {
      await api.post("/film/voice/lock", { voice: sel });
      toast.success(`Re-rendering the film with "${sel}" — about 2 minutes…`);
      poll();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start the re-render."); setBusy(false); }
  };

  if (!info) return null;
  return (
    <div className="rud-card rounded-2xl p-6 mt-6" data-testid="voice-picker">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><AudioLines size={14} /> Narrator voice</div>
      <p className="text-sec text-sm mt-1">Preview a few calm, composed voices for Anxya. Current: <b className="text-[var(--rud-text)]">{info.voice}</b>.</p>
      <audio ref={audioRef} className="hidden" />
      <div className="grid sm:grid-cols-2 gap-2 mt-4">
        {info.voices.map((v) => (
          <button key={v.id} data-testid={`voice-${v.id}`} onClick={() => preview(v.id)}
            className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${sel === v.id ? "border-brand bg-brand/5" : "border-rud hover:border-brand/50"}`}>
            <span className="w-8 h-8 rounded-full grid place-items-center bg-brand/15 border border-brand/40 shrink-0">
              {loading === v.id ? <Loader2 size={14} className="animate-spin text-brand" /> : <Play size={14} className="text-brand" />}
            </span>
            <span className="min-w-0">
              <span className="text-sm font-medium flex items-center gap-1.5">{v.name} {sel === v.id && <Check size={13} className="text-brand" />}</span>
              <span className="block text-[11px] text-sec">{v.desc}</span>
            </span>
          </button>
        ))}
      </div>
      {info.is_admin ? (
        <button data-testid="voice-lock" onClick={lock} disabled={busy}
          className="btn-primary rounded-full h-10 px-6 text-sm flex items-center gap-2 mt-4 disabled:opacity-60">
          {busy ? <><Loader2 size={15} className="animate-spin" /> Re-rendering…</> : <><Lock size={15} /> Lock "{sel}" &amp; re-render film</>}
        </button>
      ) : (
        <p className="text-[11px] text-sec mt-4">Tap any voice to hear a sample. Locking a voice for the film is admin-only.</p>
      )}
    </div>
  );
}

export default function Watch() {
  const [ver, setVer] = useState("");
  useEffect(() => { api.get("/film/voice").then(({ data }) => setVer(data.version)).catch(() => {}); }, []);
  const v = ver ? `?v=${ver}` : "";
  return (
    <div className="max-w-4xl mx-auto px-5 pt-24 pb-20" data-testid="watch-page">
      <Seo
        title="Meet My Kind — the sovereign, ad-free, AI-native platform"
        path="/watch"
        description="A 2-minute tour with Anxya: honest ad-free search, Social, Health, Habits, Book Interpretation, Product Safety, Education, Community, Travel, Events, Food, Good News, Galactopedia & Daily Practice — where you own your data and earn from it."
        image="https://mykind.app/og/watch.jpg"
      />
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-sec hover:text-brand"><ArrowLeft size={15} /> Back</Link>

      <div className="flex items-center gap-2 mt-3">
        <Sparkles className="text-brand" />
        <h1 className="font-serif text-4xl tracking-tight"><KindText text="Meet My Kind" /></h1>
      </div>
      <p className="text-sec text-base mt-2 max-w-2xl">
        A short tour with Anxya — the sovereign, ad-free, AI-native platform where you own your data and earn from it.
      </p>

      <div className="rud-card rounded-2xl overflow-hidden mt-6" data-testid="watch-video-wrap">
        <video key={ver} data-testid="intro-video" src={`/anxya-intro.mp4${v}`} poster={`/og/watch.jpg${v}`} controls playsInline
          className="w-full h-auto bg-black" style={{ aspectRatio: "16/9" }} />
      </div>

      <div className="flex flex-wrap gap-3 mt-5">
        <ShareButton slug="watch" title="Meet My Kind" text="A 2-minute tour of My Kind with Anxya." label="Share the film"
          className="btn-primary rounded-full h-10 px-6 text-sm flex items-center gap-2" />
        <a href={`/anxya-intro.mp4${v}`} download data-testid="watch-download" className="btn-ghost rounded-full h-10 px-5 text-sm">Download video</a>
        <Link to="/auth" data-testid="watch-cta" className="btn-ghost rounded-full h-10 px-6 text-sm">Join My Kind — it's free</Link>
      </div>

      <VoicePicker />
    </div>
  );
}
